import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  CardContent,
  Divider,
  Box,
  Stack,
} from '@mui/material';
import type { InventorySnapshotVolume } from '@repo/api-gw-sdk';

import { EncryptionComponent } from '@/components/encryption/encryptionComponent';
import { TagsSection } from '@/components/tags/tagsSection';
import { VolumeSettingsSection } from '@/components/volumes/volumeSettings';
import type { StepProps } from '@/components/wizard/StepProps';

import type { ConvertToAMIState } from '../convertToAMIWizard';
import {
  type VolumeConfigState,
  type RestoreEc2InstanceState,
} from '../restoreEc2InstanceWizard';

export const SingleVolumeConfig = ({
  vol,
  initialSettings,
  volumeSettings,
  setWizardState,
  restoreAccountId,
  regionName,
  showEncryption,
  showTags = true,
}: {
  vol: InventorySnapshotVolume;
  initialSettings: VolumeConfigState;
  volumeSettings: VolumeConfigState;
  setWizardState: StepProps<
    RestoreEc2InstanceState | ConvertToAMIState
  >['setWizardState'];
  restoreAccountId: string;
  regionName: string;
  showEncryption: boolean;
  showTags?: boolean;
}) => {
  const setVolumesConfigState = <T extends keyof VolumeConfigState>(
    field: T,
    value: VolumeConfigState[T]
  ) => {
    setWizardState((state) => {
      const volumesConfigState = state.crossStepsState.volumesConfigState?.map(
        (volume) =>
          volume.providerVolumeId === vol.providerVolumeId
            ? {
                ...volume,
                [field]: value,
              }
            : volume
      );
      return {
        ...state,
        crossStepsState: {
          ...state.crossStepsState,
          volumesConfigState,
        },
      };
    });
  };

  return (
    <Accordion defaultExpanded={false} className='my-[2px]'>
      <AccordionSummary>
        <Typography variant='body1'>{vol.providerVolumeId}</Typography>
      </AccordionSummary>
      <AccordionDetails className='p-0'>
        <Box sx={showEncryption ? {} : { display: 'none' }}>
          <CardContent className='p-0'>
            <EncryptionComponent
              encryption={volumeSettings.encryption}
              accountId={restoreAccountId}
              regionName={regionName}
              onChange={(encryption) =>
                setVolumesConfigState('encryption', encryption)
              }
            />
          </CardContent>
        </Box>
        {showEncryption && <Divider />}
        <Stack
          gap='24px'
          divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          className='my-[40px]'
        >
          <CardContent className='p-0'>
            <VolumeSettingsSection
              initialSettings={initialSettings?.volumeSettings}
              settings={volumeSettings?.volumeSettings}
              keepOriginalSettings={volumeSettings.keepOriginalSettings}
              setKeepOriginalSettings={(keepOriginalSettings) =>
                setVolumesConfigState(
                  'keepOriginalSettings',
                  keepOriginalSettings
                )
              }
              onChange={(volumeSettings) =>
                setVolumesConfigState('volumeSettings', volumeSettings)
              }
            />
          </CardContent>
          {showTags && (
            <CardContent className='p-0'>
              <TagsSection
                initialTags={initialSettings.tags || {}}
                tags={volumeSettings.tags || initialSettings.tags || {}}
                keepOriginalTags={volumeSettings.keepOriginalTags}
                setKeepOriginalTags={(keepOriginalTags: boolean) =>
                  setVolumesConfigState('keepOriginalTags', keepOriginalTags)
                }
                onChange={(tags) => setVolumesConfigState('tags', tags)}
              />
            </CardContent>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
